'use client';

import { useState } from 'react';
import { MailCheck, X, Loader2 } from 'lucide-react';
import { useAuth, ResendVerificationError } from '@/context/AuthContext';
import { useCountdown } from '@/hooks/useCountdown';
import { useTranslations } from 'next-intl';

const RESEND_COOLDOWN_SECONDS = 60;

export function EmailVerificationBanner() {
  const t = useTranslations('emailVerificationBanner');
  const { firebaseUser, resendVerificationEmail } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, startCooldown] = useCountdown();

  // Only show for email/password users with unverified email
  const isEmailProvider = firebaseUser?.providerData.some((p) => p.providerId === 'password');
  if (!firebaseUser || firebaseUser.emailVerified || !isEmailProvider || dismissed) return null;

  const handleResend = async () => {
    setIsSending(true);
    setError(null);
    try {
      await resendVerificationEmail();
      setSent(true);
      startCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      if (err instanceof ResendVerificationError && err.status === 429) {
        startCooldown(RESEND_COOLDOWN_SECONDS);
      }
      setError(err instanceof Error ? err.message : t('sendError'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
      <div className="max-w-5xl mx-auto flex items-center gap-3">
        <MailCheck className="w-4 h-4 text-amber-600 shrink-0" />
        <p className="text-sm text-amber-800 flex-1">
          {sent ? t('sent') : t('notVerified')}
          {' '}
          <button
            onClick={handleResend}
            disabled={isSending || cooldown > 0}
            className="font-medium underline underline-offset-2 hover:text-amber-900 disabled:opacity-50 inline-flex items-center gap-1"
          >
            {isSending && <Loader2 className="w-3 h-3 animate-spin" />}
            {cooldown > 0 ? t('resendCooldown', { seconds: cooldown }) : t('resend')}
          </button>
          .
          {error && <span className="ml-2 text-red-600">{error}</span>}
        </p>
        <button
          onClick={() => setDismissed(true)}
          className="text-amber-500 hover:text-amber-700 shrink-0"
          aria-label={t('close')}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
